import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  CalendarDays,
  Check,
  Clock,
  Moon,
  Scissors,
  Sun,
  Sunset,
  UserRound,
} from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { AvailabilitySlot, CreateAppointmentInput, PublicShop } from '@agenda-facil/contracts';
import { Avatar, Button, Field, Input, Skeleton, Surface, cn } from '@agenda-facil/ui';
import { Brand } from '../../components/brand';
import { ErrorState, PageLoader } from '../../components/query-feedback';
import { TurnstileWidget } from '../../components/turnstile-widget';
import {
  buildAvailabilityGroups,
  type AvailabilityPeriod,
} from '../../features/booking/availability-groups';
import {
  TicketConfirmation,
  type AppointmentResult,
} from '../../features/booking/ticket-confirmation';
import { api, ApiClientError } from '../../lib/api';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

type BookingStep = 1 | 2 | 3 | 4;

const bookingSteps: Array<{ id: BookingStep; label: string }> = [
  { id: 1, label: 'Serviço' },
  { id: 2, label: 'Profissional' },
  { id: 3, label: 'Horário' },
  { id: 4, label: 'Dados' },
];

const periodIcons: Record<AvailabilityPeriod, typeof Sun> = {
  MORNING: Sun,
  AFTERNOON: Sunset,
  EVENING: Moon,
};

function initialBookingDate(value: string | null): string {
  const today = format(new Date(), 'yyyy-MM-dd');
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= today ? value : today;
}

export function BookingPage() {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<BookingStep>(1);
  const [serviceId, setServiceId] = useState('');
  const [barberId, setBarberId] = useState(() => searchParams.get('barberId') ?? 'any');
  const [date, setDate] = useState(() => initialBookingDate(searchParams.get('date')));
  const [slot, setSlot] = useState<AvailabilitySlot>();
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '' });
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [error, setError] = useState('');

  const shop = useQuery({
    queryKey: ['public-shop', slug],
    queryFn: () => api.get<PublicShop>(`/public/shops/${slug}`),
  });

  const slots = useQuery({
    queryKey: ['availability', slug, serviceId, barberId, date],
    queryFn: () =>
      api.get<AvailabilitySlot[]>(
        `/public/shops/${slug}/availability?serviceId=${serviceId}&barberId=${barberId}&date=${date}`,
      ),
    enabled: !!serviceId && !!barberId && !!date,
  });

  const create = useMutation({
    mutationFn: (input: CreateAppointmentInput) =>
      api.post<AppointmentResult>(`/public/shops/${slug}/appointments`, input),
    onError: (caught) => {
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : 'Não foi possível confirmar o agendamento.',
      );
      void slots.refetch();
      setSlot(undefined);
    },
  });

  const service = shop.data?.services.find((item) => item.id === serviceId);
  const setToken = useCallback((token?: string) => setTurnstileToken(token), []);
  const ready = Boolean(
    service &&
    slot &&
    customer.name.trim().length >= 2 &&
    customer.phone.length >= 10 &&
    customer.email.includes('@') &&
    Boolean(turnstileToken),
  );

  useEffect(() => {
    if (!shop.data || barberId === 'any') return;
    if (!shop.data.barbers.some((barber) => barber.id === barberId)) setBarberId('any');
  }, [barberId, shop.data]);

  // Next 7 days for fast mobile carousel date picking
  const next7Days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(today, i);
      const iso = format(d, 'yyyy-MM-dd');
      const dayName = i === 0 ? 'Hoje' : i === 1 ? 'Amanhã' : format(d, 'EEE', { locale: ptBR });
      const dayNum = format(d, 'dd/MM');
      return { iso, dayName, dayNum };
    });
  }, []);

  const availabilityGroups = useMemo(
    () => buildAvailabilityGroups(slots.data ?? [], shop.data?.timezone ?? 'America/Sao_Paulo'),
    [shop.data?.timezone, slots.data],
  );

  const selectedBarberName =
    barberId === 'any'
      ? 'Qualquer profissional'
      : (shop.data?.barbers.find((barber) => barber.id === barberId)?.name ??
        'Qualquer profissional');

  const canContinue =
    currentStep === 1
      ? Boolean(service)
      : currentStep === 2
        ? Boolean(barberId)
        : currentStep === 3
          ? Boolean(slot)
          : ready;

  const primaryLabel =
    currentStep === 1
      ? 'Escolher profissional'
      : currentStep === 2
        ? 'Ver horários'
        : currentStep === 3
          ? 'Informar meus dados'
          : 'Confirmar agendamento';

  const submitBooking = () => {
    if (!ready || !slot) return;
    create.mutate({
      serviceId,
      barberId: slot.barberId,
      startsAt: slot.startsAt,
      customer,
      ...(turnstileToken ? { turnstileToken } : {}),
    });
  };

  const handlePrimaryAction = () => {
    if (!canContinue) return;
    if (currentStep === 4) {
      submitBooking();
      return;
    }
    setCurrentStep((current) => Math.min(4, current + 1) as BookingStep);
  };

  const goBack = () => setCurrentStep((current) => Math.max(1, current - 1) as BookingStep);

  if (shop.isLoading) return <PageLoader label="Buscando barbearia…" />;
  if (shop.isError || !shop.data)
    return (
      <div className="page-shell py-16">
        <ErrorState message="Barbearia não encontrada." />
      </div>
    );

  if (create.data && service && slot)
    return (
      <TicketConfirmation
        shop={shop.data}
        service={service}
        slot={slot}
        appointment={create.data}
      />
    );

  return (
    <div className="min-h-screen bg-[var(--background)] pb-32 lg:pb-12">
      <header className="sticky top-0 z-30 border-b border-[var(--border)] bg-[var(--surface)]/90 backdrop-blur-md">
        <div className="page-shell flex h-16 items-center justify-between">
          <Link
            to={`/barbearia/${slug}`}
            className="flex min-h-11 items-center gap-2 text-xs font-black uppercase text-[var(--muted-foreground)] transition hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <ArrowLeft size={16} aria-hidden="true" /> Voltar
          </Link>
          <Brand compact />
        </div>
      </header>

      <main className="page-shell py-6 sm:py-10">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 sm:mb-8">
            <span className="eyebrow">{shop.data.name}</span>
            <h1 className="display mt-2 text-3xl font-extrabold sm:text-4xl">
              Reserve seu horário
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--muted-foreground)]">
              Uma escolha por vez. Você revisa tudo antes de confirmar.
            </p>
          </div>

          <nav aria-label="Etapas do agendamento" className="mb-6 overflow-x-auto pb-1">
            <ol className="grid min-w-[460px] grid-cols-4 gap-2">
              {bookingSteps.map((step) => {
                const isCurrent = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      disabled={!isCompleted}
                      onClick={() => isCompleted && setCurrentStep(step.id)}
                      aria-current={isCurrent ? 'step' : undefined}
                      className={cn(
                        'flex min-h-12 w-full items-center gap-2 rounded-xl border px-3 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-default',
                        isCurrent &&
                          'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-foreground)]',
                        isCompleted &&
                          'border-[var(--border)] bg-[var(--surface)] text-[var(--foreground)] hover:bg-[var(--surface-raised)]',
                        !isCurrent &&
                          !isCompleted &&
                          'border-transparent bg-[var(--surface-raised)] text-[var(--muted-foreground)] opacity-65',
                      )}
                    >
                      <span
                        className={cn(
                          'grid size-6 shrink-0 place-items-center rounded-full text-[10px] font-black',
                          isCurrent
                            ? 'bg-[color-mix(in_srgb,var(--brand-foreground)_16%,transparent)]'
                            : 'bg-[var(--input)]',
                        )}
                      >
                        {isCompleted ? <Check size={13} aria-hidden="true" /> : step.id}
                      </span>
                      <span className="text-xs font-extrabold">{step.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="min-w-0">
              {currentStep === 1 && (
                <BookingSection number="1" title="Escolha o serviço" icon={Scissors}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {shop.data.services.map((item) => (
                      <ChoiceButton
                        key={item.id}
                        selected={serviceId === item.id}
                        onClick={() => {
                          setServiceId(item.id);
                          setSlot(undefined);
                        }}
                      >
                        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">
                          <Scissors size={17} aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <strong className="block text-sm font-extrabold text-[var(--foreground)]">
                            {item.name}
                          </strong>
                          {item.description && (
                            <span className="mt-1 line-clamp-2 text-xs leading-5 text-[var(--muted-foreground)]">
                              {item.description}
                            </span>
                          )}
                          <span className="mt-2 flex items-center gap-1 text-xs font-bold text-[var(--muted-foreground)]">
                            <Clock size={13} aria-hidden="true" /> {item.durationMinutes} min
                          </span>
                        </div>
                        <strong className="shrink-0 text-sm font-black text-[var(--accent)]">
                          {currency.format(item.priceCents / 100)}
                        </strong>
                      </ChoiceButton>
                    ))}
                  </div>
                  {shop.data.services.length === 0 && (
                    <p className="rounded-2xl border border-[var(--border)] bg-[var(--input)] p-5 text-center text-sm font-bold text-[var(--muted-foreground)]">
                      Nenhum serviço está disponível para agendamento online.
                    </p>
                  )}
                  <DesktopStepActions
                    primaryLabel={primaryLabel}
                    onPrimary={handlePrimaryAction}
                    disabled={!canContinue}
                  />
                </BookingSection>
              )}

              {currentStep === 2 && (
                <BookingSection number="2" title="Escolha quem vai atender" icon={UserRound}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ChoiceButton
                      selected={barberId === 'any'}
                      onClick={() => {
                        setBarberId('any');
                        setSlot(undefined);
                      }}
                    >
                      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[var(--brand)] text-[var(--brand-foreground)]">
                        <UserRound size={19} aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <strong className="block text-sm font-extrabold text-[var(--foreground)]">
                          Qualquer profissional
                        </strong>
                        <span className="mt-0.5 block text-xs text-[var(--muted-foreground)]">
                          Primeiro horário disponível
                        </span>
                      </div>
                    </ChoiceButton>
                    {shop.data.barbers.map((barber) => (
                      <ChoiceButton
                        key={barber.id}
                        selected={barberId === barber.id}
                        onClick={() => {
                          setBarberId(barber.id);
                          setSlot(undefined);
                        }}
                      >
                        <Avatar name={barber.name} size="md" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <strong className="block truncate text-sm font-extrabold text-[var(--foreground)]">
                            {barber.name}
                          </strong>
                        </div>
                      </ChoiceButton>
                    ))}
                  </div>
                  <DesktopStepActions
                    showBack
                    primaryLabel={primaryLabel}
                    onBack={goBack}
                    onPrimary={handlePrimaryAction}
                    disabled={!canContinue}
                  />
                </BookingSection>
              )}

              {currentStep === 3 && (
                <BookingSection number="3" title="Escolha data e horário" icon={CalendarDays}>
                  <div className="space-y-3">
                    <p className="text-xs font-black uppercase tracking-[0.1em] text-[var(--muted-foreground)]">
                      Data
                    </p>
                    <div className="flex snap-x gap-2 overflow-x-auto pb-2">
                      {next7Days.map((item) => {
                        const isSelected = date === item.iso;
                        return (
                          <button
                            key={item.iso}
                            type="button"
                            onClick={() => {
                              setDate(item.iso);
                              setSlot(undefined);
                            }}
                            aria-pressed={isSelected}
                            className={cn(
                              'flex min-h-16 min-w-[86px] shrink-0 snap-start flex-col items-center justify-center rounded-2xl border p-3 text-center transition-[background-color,color,border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                              isSelected
                                ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-foreground)] shadow-[var(--shadow-sm)]'
                                : 'border-[var(--border)] bg-[var(--input)] text-[var(--foreground)] hover:border-[var(--border-strong)]',
                            )}
                          >
                            <span className="text-[11px] font-bold uppercase opacity-80">
                              {item.dayName}
                            </span>
                            <span className="mt-0.5 text-sm font-black">{item.dayNum}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Calendar
                        size={14}
                        className="text-[var(--muted-foreground)]"
                        aria-hidden="true"
                      />
                      <label
                        htmlFor="booking-date"
                        className="text-xs text-[var(--muted-foreground)]"
                      >
                        Outra data:
                      </label>
                      <Input
                        id="booking-date"
                        type="date"
                        min={format(new Date(), 'yyyy-MM-dd')}
                        value={date}
                        onChange={(e) => {
                          setDate(e.target.value);
                          setSlot(undefined);
                        }}
                        className="max-w-[170px] py-1 text-xs"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="mb-4 text-xs font-black uppercase tracking-[0.1em] text-[var(--muted-foreground)]">
                      Horários
                    </p>

                    {slots.isLoading ? (
                      <div
                        className="grid grid-cols-3 gap-2.5 sm:grid-cols-5"
                        aria-label="Buscando horários livres"
                        role="status"
                      >
                        {Array.from({ length: 10 }, (_, index) => (
                          <Skeleton key={index} variant="button" className="h-14" />
                        ))}
                      </div>
                    ) : slots.isError ? (
                      <ErrorState
                        message="Não foi possível carregar os horários."
                        retry={() => void slots.refetch()}
                      />
                    ) : slots.data && slots.data.length > 0 ? (
                      <div className="space-y-6">
                        {availabilityGroups.map((group) => {
                          if (group.options.length === 0) return null;
                          const PeriodIcon = periodIcons[group.id];
                          return (
                            <section key={group.id} aria-labelledby={`period-${group.id}`}>
                              <h3
                                id={`period-${group.id}`}
                                className="mb-2.5 flex items-center gap-2 text-sm font-extrabold text-[var(--foreground)]"
                              >
                                <PeriodIcon
                                  size={16}
                                  className="text-[var(--accent)]"
                                  aria-hidden="true"
                                />
                                {group.label}
                              </h3>
                              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                                {group.options.map((option) => {
                                  const availableSlot = option.slot;
                                  const isSelected = Boolean(
                                    availableSlot &&
                                    slot?.startsAt === availableSlot.startsAt &&
                                    slot.barberId === availableSlot.barberId,
                                  );
                                  return (
                                    <button
                                      type="button"
                                      key={option.time}
                                      disabled={!availableSlot}
                                      aria-pressed={isSelected}
                                      aria-label={
                                        availableSlot
                                          ? `${option.time}, disponível${barberId === 'any' ? ` com ${availableSlot.barberName}` : ''}`
                                          : `${option.time}, indisponível`
                                      }
                                      onClick={() => availableSlot && setSlot(availableSlot)}
                                      className={cn(
                                        'flex min-h-14 flex-col items-center justify-center rounded-xl border p-2 text-center transition-[background-color,color,border-color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] active:translate-y-px',
                                        isSelected &&
                                          'motion-choice-selected border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-foreground)] shadow-[var(--shadow-sm)]',
                                        availableSlot &&
                                          !isSelected &&
                                          'border-[var(--border)] bg-[var(--input)] text-[var(--foreground)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-raised)]',
                                        !availableSlot &&
                                          'cursor-not-allowed border-transparent bg-[var(--surface-raised)] text-[var(--muted-foreground)] opacity-45',
                                      )}
                                    >
                                      <span className="text-sm font-black tabular-nums">
                                        {option.time}
                                      </span>
                                      {availableSlot && barberId === 'any' ? (
                                        <span className="mt-0.5 max-w-full truncate text-[9px] font-bold opacity-75">
                                          {availableSlot.barberName}
                                        </span>
                                      ) : !availableSlot ? (
                                        <span className="mt-0.5 text-[9px] font-bold">
                                          Indisponível
                                        </span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="rounded-2xl border border-[color-mix(in_srgb,var(--warning)_24%,var(--border))] bg-[var(--warning-soft)] p-4 text-center text-xs font-extrabold text-[var(--warning)]">
                        Não há horários livres nesta data. Escolha outro dia.
                      </p>
                    )}
                  </div>
                  <DesktopStepActions
                    showBack
                    primaryLabel={primaryLabel}
                    onBack={goBack}
                    onPrimary={handlePrimaryAction}
                    disabled={!canContinue}
                  />
                </BookingSection>
              )}

              {currentStep === 4 && (
                <BookingSection number="4" title="Seus dados" icon={UserRound}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Nome completo" htmlFor="customer-name">
                      <Input
                        id="customer-name"
                        autoComplete="name"
                        placeholder="Carlos Silva"
                        required
                        value={customer.name}
                        onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      />
                    </Field>
                    <Field label="WhatsApp ou telefone" htmlFor="customer-phone">
                      <Input
                        id="customer-phone"
                        autoComplete="tel"
                        placeholder="(11) 99999-9999"
                        required
                        value={customer.phone}
                        onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                      />
                    </Field>
                    <Field
                      label="E-mail de confirmação"
                      htmlFor="customer-email"
                      className="sm:col-span-2"
                    >
                      <Input
                        id="customer-email"
                        type="email"
                        autoComplete="email"
                        placeholder="seu@email.com"
                        required
                        value={customer.email}
                        onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <TurnstileWidget onToken={setToken} />
                  </div>

                  {error && (
                    <p
                      role="alert"
                      className="mt-4 rounded-xl border border-[color-mix(in_srgb,var(--danger)_24%,var(--border))] bg-[var(--danger-soft)] p-3 text-xs font-bold text-[var(--danger)]"
                    >
                      {error}
                    </p>
                  )}

                  <DesktopStepActions
                    showBack
                    primaryLabel={primaryLabel}
                    onBack={goBack}
                    onPrimary={handlePrimaryAction}
                    disabled={!canContinue}
                    loading={create.isPending}
                  />
                </BookingSection>
              )}
            </div>

            <aside className="hidden lg:sticky lg:top-20 lg:block lg:self-start">
              <Surface className="p-6">
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[var(--accent)]">
                  Seu agendamento
                </p>
                <h2 className="mt-2 text-lg font-black text-[var(--foreground)]">
                  {shop.data.name}
                </h2>

                <div className="mt-4 grid gap-3 border-t border-[var(--border)] pt-4 text-xs">
                  {service ? (
                    <SummaryRow label="Serviço" value={service.name} />
                  ) : (
                    <SummaryRow label="Serviço" value="Pendente" muted />
                  )}

                  {slot ? (
                    <>
                      <SummaryRow
                        label="Data"
                        value={formatInTimeZone(
                          slot.startsAt,
                          shop.data.timezone,
                          'dd/MM/yyyy, EEEE',
                          {
                            locale: ptBR,
                          },
                        )}
                      />
                      <SummaryRow
                        label="Horário"
                        value={formatInTimeZone(slot.startsAt, shop.data.timezone, 'HH:mm')}
                      />
                      <SummaryRow label="Profissional" value={slot.barberName} />
                    </>
                  ) : (
                    <SummaryRow label="Data e horário" value="A escolher" muted />
                  )}
                </div>

                {service && (
                  <div className="mt-5 flex items-center justify-between border-t border-[var(--border)] pt-4">
                    <span className="text-xs font-bold text-[var(--muted-foreground)]">
                      Valor total
                    </span>
                    <span className="text-xl font-black text-[var(--accent)]">
                      {currency.format(service.priceCents / 100)}
                    </span>
                  </div>
                )}
              </Surface>
            </aside>
          </div>
        </div>
      </main>

      <div className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-[var(--border)] bg-[var(--surface)] px-3 pt-3 lg:hidden">
        <div className="mx-auto flex max-w-lg items-center gap-2">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={goBack}
              aria-label="Voltar uma etapa"
              className="grid size-11 shrink-0 place-items-center rounded-xl border border-[var(--border)] text-[var(--muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              <ArrowLeft size={17} aria-hidden="true" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-black uppercase tracking-[0.1em] text-[var(--muted-foreground)]">
              Etapa {currentStep} de 4
            </p>
            <p className="truncate text-xs font-extrabold text-[var(--foreground)]">
              {currentStep === 1 && (service?.name ?? 'Escolha um serviço')}
              {currentStep === 2 && selectedBarberName}
              {currentStep === 3 &&
                (slot
                  ? `${formatInTimeZone(slot.startsAt, shop.data.timezone, 'dd/MM')} às ${formatInTimeZone(slot.startsAt, shop.data.timezone, 'HH:mm')}`
                  : 'Escolha data e horário')}
              {currentStep === 4 &&
                (service ? currency.format(service.priceCents / 100) : 'Revisar dados')}
            </p>
          </div>
          <Button
            size="sm"
            className="min-h-11 shrink-0 px-4"
            disabled={!canContinue}
            loading={create.isPending}
            onClick={handlePrimaryAction}
          >
            {currentStep === 4 ? 'Confirmar' : 'Continuar'}
            {currentStep < 4 && <ArrowRight size={15} aria-hidden="true" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BookingSection({
  number,
  title,
  icon: Icon,
  children,
}: {
  number: string;
  title: string;
  icon: typeof Scissors;
  children: ReactNode;
}) {
  return (
    <section className="motion-step-in">
      <Surface className="p-5 sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="grid size-9 place-items-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)] font-black">
            <Icon size={18} aria-hidden="true" />
          </span>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[.14em] text-[var(--muted-foreground)]">
              Etapa {number}
            </p>
            <h2 className="font-extrabold text-base text-[var(--foreground)]">{title}</h2>
          </div>
        </div>
        {children}
      </Surface>
    </section>
  );
}

function ChoiceButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex min-h-20 w-full items-center gap-3 rounded-2xl border p-4 text-left text-xs transition-[background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] active:translate-y-px',
        selected
          ? 'motion-choice-selected border-[var(--brand)] bg-[color-mix(in_srgb,var(--brand)_8%,var(--surface))] shadow-[0_0_0_1px_var(--brand)]'
          : 'border-[var(--border)] bg-[var(--input)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-raised)]',
      )}
    >
      {children}
      {selected && <Check size={18} className="shrink-0 text-[var(--brand)]" aria-hidden="true" />}
    </button>
  );
}

function DesktopStepActions({
  showBack = false,
  primaryLabel,
  onBack,
  onPrimary,
  disabled,
  loading = false,
}: {
  showBack?: boolean;
  primaryLabel: string;
  onBack?: () => void;
  onPrimary: () => void;
  disabled: boolean;
  loading?: boolean;
}) {
  return (
    <div className="mt-6 hidden items-center justify-between border-t border-[var(--border)] pt-5 lg:flex">
      {showBack ? (
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> Voltar
        </Button>
      ) : (
        <span />
      )}
      <Button onClick={onPrimary} disabled={disabled} loading={loading}>
        {primaryLabel}
        {!loading && <ArrowRight size={16} aria-hidden="true" />}
      </Button>
    </div>
  );
}

function SummaryRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--muted-foreground)] font-medium">{label}</span>
      <strong
        className={cn(
          'text-right font-extrabold capitalize',
          muted && 'text-[var(--muted-foreground)]',
        )}
      >
        {value}
      </strong>
    </div>
  );
}
